import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Slider,
  IconButton,
  Tooltip,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";

import logger from '../../../../../../utils/logger';

const EXERCISE_FREQUENCY = [
  { value: 0, label: "Never" },
  { value: 1, label: "1-2 times/week" },
  { value: 2, label: "3-4 times/week" },
  { value: 3, label: "5+ times/week" },
];

const SLEEP_QUALITY = [
  { value: 1, label: "Poor (< 5 hours)" },
  { value: 2, label: "Fair (5-6 hours)" },
  { value: 3, label: "Good (7-8 hours)" },
  { value: 4, label: "Excellent (> 8 hours)" },
];

const STRESS_LEVELS = [
  { value: 1, label: "Low", description: "Rarely feel stressed" },
  { value: 2, label: "Moderate", description: "Occasionally feel stressed" },
  { value: 3, label: "High", description: "Frequently feel stressed" },
  { value: 4, label: "Severe", description: "Constantly feel stressed" },
];

const SMOKER_STATUS = [
  { value: "never", label: "Never Smoked" },
  { value: "former", label: "Former Smoker" },
  { value: "current", label: "Current Smoker" },
];

const AdvancedAssessmentStep = ({ data, previousData, member, onUpdate }) => {
  logger.info("AdvancedAssessmentStep - Full Previous Data:", previousData);

  // Get base life expectancy from previous steps
  const getBaseLifeExpectancy = () => {
    // Try to get from recommended assessment first
    const recommendedValue = previousData?.predicted_life_expectancy_rec;
    if (recommendedValue) {
      return parseFloat(recommendedValue);
    }

    // Fall back to basic assessment
    const basicValue = previousData?.calculated_life_expectancy;
    if (basicValue) {
      return parseFloat(basicValue);
    }

    return 0;
  };

  // Store the base life expectancy in a ref to prevent it from changing
  const baseLifeExpectancyRef = React.useRef(getBaseLifeExpectancy());

  const initialFormData = {
    // Use data if available, otherwise use neutral defaults
    height_feet: data?.height ? Math.floor(data.height / 12) : "",
    height_inches: data?.height ? data.height % 12 : "",
    weight: data?.weight || "",
    calculated_bmi: data?.calculated_bmi || 0,
    smoker_status: data?.smoker_status || "never",
    sleep_quality: data?.sleep_quality || 3,
    exercise_frequency: data?.exercise_frequency || 0,
    stress_level: data?.stress_level || 1,
    predicted_life_expectancy_adv:
      data?.predicted_life_expectancy_adv || baseLifeExpectancyRef.current,
  };

  const [formData, setFormData] = useState(initialFormData);

  const calculateBMI = (values = formData) => {
    if (values.height_feet && values.height_inches && values.weight) {
      const totalHeightInches = values.height_feet * 12 + values.height_inches;
      const heightInMeters = totalHeightInches * 0.0254;
      const weightKg = values.weight * 0.453592;
      return Number((weightKg / (heightInMeters * heightInMeters)).toFixed(1));
    }
    return 0;
  };

  const calculateAdjustments = (formValues) => {
    const bmi = calculateBMI(formValues);
    const baseExpectancy = baseLifeExpectancyRef.current;
    let adjustment = 0;

    // BMI adjustments
    if (bmi > 0) {
      if (bmi < 18.5)
        adjustment -= 2; // Underweight
      else if (bmi >= 30)
        adjustment -= 3; // Obese
      else if (bmi >= 25) adjustment -= 1; // Overweight
    }

    // Smoking adjustments
    if (formValues.smoker_status === "current") adjustment -= 10;
    else if (formValues.smoker_status === "former") adjustment -= 3;

    // Sleep quality adjustments
    adjustment += formValues.sleep_quality - 2;

    // Exercise frequency adjustments
    adjustment += formValues.exercise_frequency - 1;

    // Stress level adjustments
    adjustment -= formValues.stress_level - 1;

    const finalExpectancy = Math.max(
      baseExpectancy + adjustment,
      baseExpectancy - 15,
    );

    return {
      bmi,
      finalExpectancy,
      adjustment,
    };
  };

  // Calculate initial values
  useEffect(() => {
    const { bmi, finalExpectancy } = calculateAdjustments(formData);

    if (
      formData.predicted_life_expectancy_adv !== finalExpectancy ||
      formData.calculated_bmi !== bmi
    ) {
      const newFormData = {
        ...formData,
        calculated_bmi: bmi,
        predicted_life_expectancy_adv: finalExpectancy,
      };

      setFormData(newFormData);

      onUpdate({
        ...newFormData,
        height:
          formData.height_feet && formData.height_inches
            ? formData.height_feet * 12 + formData.height_inches
            : null,
        weight: formData.weight || null,
        assessment_date: new Date().toISOString(),
        isValid: isFormValid(),
      });
    }
  }, []);

  // Effect to handle form field changes
  useEffect(() => {
    const { bmi, finalExpectancy, adjustment } = calculateAdjustments(formData);
    logger.info("Calculating adjustments:", {
      baseExpectancy: baseLifeExpectancyRef.current,
      adjustment,
      finalExpectancy,
      currentFormData: formData,
    });

    const updatedFormData = {
      ...formData,
      calculated_bmi: bmi,
      predicted_life_expectancy_adv: finalExpectancy,
    };

    setFormData(updatedFormData);

    // Notify parent of updates
    onUpdate({
      ...updatedFormData,
      height:
        formData.height_feet && formData.height_inches
          ? formData.height_feet * 12 + formData.height_inches
          : null,
      weight: formData.weight || null,
      assessment_date: new Date().toISOString(),
      isValid: isFormValid(),
    });
  }, [
    formData.height_feet,
    formData.height_inches,
    formData.weight,
    formData.smoker_status,
    formData.sleep_quality,
    formData.exercise_frequency,
    formData.stress_level,
  ]);

  // Effect to handle previousData changes
  useEffect(() => {
    if (previousData?.predicted_life_expectancy_rec) {
      baseLifeExpectancyRef.current = parseFloat(
        previousData.predicted_life_expectancy_rec,
      );
      const { bmi, finalExpectancy } = calculateAdjustments(formData);

      const updatedFormData = {
        ...formData,
        calculated_bmi: bmi,
        predicted_life_expectancy_adv: finalExpectancy,
      };

      setFormData(updatedFormData);

      onUpdate({
        ...updatedFormData,
        height:
          formData.height_feet && formData.height_inches
            ? formData.height_feet * 12 + formData.height_inches
            : null,
        weight: formData.weight || null,
        assessment_date: new Date().toISOString(),
        isValid: isFormValid(),
      });
    }
  }, [previousData?.predicted_life_expectancy_rec]);

  const isFormValid = () => {
    return (
      formData.height_feet >= 3 &&
      formData.height_feet <= 8 &&
      formData.height_inches >= 0 &&
      formData.height_inches < 12 &&
      formData.weight > 0 &&
      formData.smoker_status &&
      formData.sleep_quality > 0 &&
      formData.exercise_frequency >= 0 &&
      formData.stress_level > 0
    );
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { label: "Underweight", color: "warning.main" };
    if (bmi < 25) return { label: "Normal", color: "success.main" };
    if (bmi < 30) return { label: "Overweight", color: "warning.main" };
    return { label: "Obese", color: "error.main" };
  };

  const handleInputChange = (field) => (event) => {
    const value =
      event.target.type === "number"
        ? parseFloat(event.target.value)
        : event.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Advanced Health Assessment
      </Typography>

      {/* Physical Measurements */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Physical Measurements
          <Tooltip title="Enter your current height and weight for BMI calculation">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Height (feet)"
                type="number"
                value={formData.height_feet}
                onChange={handleInputChange("height_feet")}
                InputProps={{ inputProps: { min: 3, max: 8 } }}
                sx={{ width: "120px" }}
              />
              <TextField
                label="Height (inches)"
                type="number"
                value={formData.height_inches}
                onChange={handleInputChange("height_inches")}
                InputProps={{ inputProps: { min: 0, max: 11 } }}
                sx={{ width: "120px" }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Weight"
              type="number"
              value={formData.weight}
              onChange={handleInputChange("weight")}
              fullWidth
              InputProps={{
                inputProps: { min: 50, max: 500 },
                endAdornment: (
                  <InputAdornment position="end">lbs</InputAdornment>
                ),
              }}
            />
          </Grid>
          {formData.calculated_bmi > 0 && (
            <Grid item xs={12}>
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography variant="h4" gutterBottom>
                  BMI: {formData.calculated_bmi}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ color: getBMICategory(formData.calculated_bmi).color }}
                >
                  {getBMICategory(formData.calculated_bmi).label}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Lifestyle Factors */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Lifestyle Factors
        </Typography>

        {/* Smoking Status */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" gutterBottom>
            Smoking Status
            <Tooltip title="Your current smoking habits">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={formData.smoker_status}
              onChange={handleInputChange("smoker_status")}
            >
              {SMOKER_STATUS.map((status) => (
                <FormControlLabel
                  key={status.value}
                  value={status.value}
                  control={<Radio />}
                  label={status.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Sleep Quality */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" gutterBottom>
            Sleep Quality
            <Tooltip title="Average hours of sleep per night">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          <TextField
            select
            value={formData.sleep_quality}
            onChange={handleInputChange("sleep_quality")}
            fullWidth
          >
            {SLEEP_QUALITY.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Exercise Frequency */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" gutterBottom>
            Exercise Frequency
            <Tooltip title="How often do you engage in physical activity?">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          <TextField
            select
            value={formData.exercise_frequency}
            onChange={handleInputChange("exercise_frequency")}
            fullWidth
          >
            {EXERCISE_FREQUENCY.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Stress Level */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" gutterBottom>
            Stress Level
            <Tooltip title="Your perceived level of daily stress">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          <Slider
            value={formData.stress_level}
            onChange={(_, value) =>
              handleInputChange("stress_level")({ target: { value } })
            }
            step={1}
            marks={STRESS_LEVELS.map((level) => ({
              value: level.value,
              label: level.label,
            }))}
            min={1}
            max={4}
            sx={{ mt: 2 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {
              STRESS_LEVELS.find(
                (level) => level.value === formData.stress_level,
              )?.description
            }
          </Typography>
        </Box>
      </Paper>

      {/* Final Life Expectancy */}
      <Paper
        sx={{
          p: 3,
          backgroundColor: "primary.light",
          color: "primary.contrastText",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Final Adjusted Life Expectancy
        </Typography>
        <Typography variant="h3">
          {formData.predicted_life_expectancy_adv} years
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Based on all health factors and lifestyle choices
        </Typography>
      </Paper>
    </Box>
  );
};

export default AdvancedAssessmentStep;
